import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Input, Pagination, Space, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import {
	CloseCircleOutlined,
	EyeInvisibleOutlined,
	EyeOutlined,
	PlusCircleOutlined,
	SearchOutlined,
} from '@ant-design/icons';
import { AsyncLink, PhoneLink } from 'components';
import { Page } from '../../components.js';
import { Client } from 'types';
import { Formatting } from '../../utils';
import { useClients, useFilters, useUsers } from 'reducers';
import styled from 'styled-components';

import '../../styles/pages/clients/clients.scss';
import { red } from '@ant-design/colors';

const Wrapper = styled.div`
	.ant-table-row {
		&.red {
			> td:first-child {
				border-left: 8px solid ${red[5]};
			}
		}
	}
`;

const PAGE_SIZE = 50;

export const Clients: React.FC = () => {
	const navigate = useNavigate();

	const [filters, setFilters] = useFilters<{
		search: string | null;
		removed: string;
		page: string;
	}>({
		search: null,
		removed: '0',
		page: '1',
	});
	const showRemoved = filters.removed === '1';
	const currentPage = parseInt(filters.page) || 1;

	// Reset to page 1 whenever search or removed filter changes
	useEffect(() => {
		if (filters.page !== '1') {
			setFilters('page', '1');
		}
	}, [filters.search, filters.removed]);

	const clients = useClients({
		search: filters.search ?? undefined,
		showRemoved,
	});
	const users = useUsers({});

	const firstIndex = (currentPage - 1) * PAGE_SIZE;
	const lastIndex = currentPage * PAGE_SIZE;
	const pagedClients = clients?.slice(firstIndex, lastIndex);

	const columns: ColumnsType<Client> = [
		{
			title: 'Nazwa',
			key: 'name',
			render: (_, row) => row.name,
		},
		{
			title: 'Telefon',
			key: 'phone',
			render: (_, row) => <PhoneLink phone={row.phone} />,
		},
		{
			title: 'NIP',
			key: 'nip',
			render: (_, row) => row.nip,
		},
		{
			title: 'BDO',
			key: 'bdo',
			render: (_, row) => row.bdo,
		},
		{
			title: 'Email',
			key: 'email',
			render: (_, row) => row.email,
		},
		{
			title: showRemoved ? 'Usunięty - przez' : 'Dodany - przez',
			key: 'created',
			render: (_, row) => {
				if (showRemoved) {
					if (!row.removedBy || !row.removedDate) {
						return '-';
					}

					const removedBy = users?.find((user) => user.id === row.removedBy);
					return (
						<span>
							{Formatting.epochToDate(row.removedDate)} -{' '}
							<Link to={`/panel/drivers/${row.removedBy}`}>
								{removedBy?.name} {removedBy?.surname}
							</Link>
						</span>
					);
				}

				if (!row.createdBy || !row.createdDate) {
					return '-';
				}

				const createdBy = users?.find((user) => user.id === row.createdBy);
				return (
					<span>
						{Formatting.epochToDate(row.createdDate)} -{' '}
						<Link to={`/panel/drivers/${row.createdBy}`}>
							{createdBy?.name} {createdBy?.surname}
						</Link>
					</span>
				);
			},
		},
		{
			title: 'Akcje',
			key: 'action',
			render: (_, row) => {
				const actionName = row.removedBy ? 'Przywróć' : 'Usuń';
				const actionColor = row.removedBy ? '#52C41A' : '#f5222d';
				// const action = row.removedBy ? () => restoreClient(row.id) : () => deleteClient(row.id);
				const action = async () => {};

				return (
					<Space size={5}>
						<Link to={`/panel/clients/${row.id}`}>Szczegóły</Link>
						<span>|</span>
						<AsyncLink color={actionColor} onClick={action}>
							{actionName}
						</AsyncLink>
					</Space>
				);
			},
		},
	];

	const footer = (
		<>
			<div>
				<Button type="primary" size="large" icon={<PlusCircleOutlined />} onClick={() => navigate('/panel/new-client')}>
					Dodaj nowego Klienta
				</Button>
				<Button
					type="primary"
					size="large"
					icon={showRemoved ? <EyeInvisibleOutlined /> : <EyeOutlined />}
					onClick={() => setFilters('removed', showRemoved ? '0' : '1')}
				>
					{showRemoved ? 'Ukryj' : 'Pokaż'} usuniętych klientów
				</Button>
			</div>
			<div>
				<Pagination
					current={currentPage}
					pageSize={PAGE_SIZE}
					total={clients?.length ?? 0}
					onChange={(page) => setFilters('page', String(page))}
					showTotal={(total, range) => `${range[0]}-${range[1]} z ${total}`}
					showSizeChanger={false}
				/>
			</div>
		</>
	);

	return (
		<Page title="Klienci" footer={footer}>
			<Wrapper className="clients flex flex-dir flex-1">
				<div className="inputs flex">
					<Input
						placeholder="Wyszukaj"
						size="large"
						suffix={<SearchOutlined style={{ color: '#8C8C8C' }} />}
						value={filters.search ?? ''}
						onChange={(e) => setFilters('search', e.target.value || null)}
					/>
					<Button
						type="primary"
						size="large"
						icon={<CloseCircleOutlined />}
						onClick={() => {
							setFilters('removed', '0');
							setFilters('search', null);
						}}
					>
						Wyczyść
					</Button>
				</div>
				<Table
					columns={columns}
					rowKey="id"
					rowClassName={(row) => (row.lockedDate !== null ? 'red' : '')}
					dataSource={pagedClients ?? []}
					loading={!clients || !users}
					pagination={false}
					scroll={{ x: 'max-content' }}
				/>
			</Wrapper>
		</Page>
	);
};
